import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getVideosApi } from '../../api/videos';
import './VideoFeed.scss';

const MOCK_VIDEOS = [];

export const VideoFeedGuest = () => {
  const [activeTab, setActiveTab] = useState('Video Feed');
  const [videos, setVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showComments, setShowComments] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('Link copied to clipboard!');
  const [commentText, setCommentText] = useState('');

  const [commentsList, setCommentsList] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  const videoRef = useRef(null);
  const playerContainerRef = useRef(null);
  const wheelTimeout = useRef(null);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = false;
  const currentUserId = '';

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);

  pageRef.current = page;
  hasMoreRef.current = hasMore;
  loadingMoreRef.current = loadingMore;

  const loadMoreVideos = async () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return false;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    const nextPage = pageRef.current + 1;
    try {
      const res = await getVideosApi({ tab: activeTab, page: nextPage, limit: 100000 });
      const newItems = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      if (newItems.length > 0) {
        setVideos((prev) => {
          const existingIds = new Set(prev.map(v => v._id || v.id || v.video_id));
          const uniqueItems = newItems.filter(v => {
            const vidId = v._id || v.id || v.video_id;
            return vidId && !existingIds.has(vidId);
          });
          return uniqueItems.length > 0 ? [...prev, ...uniqueItems] : prev;
        });
        setPage(nextPage);
      }

      const hasNext = res?.pagination?.has_next_page !== undefined
        ? res.pagination.has_next_page
        : false;
      setHasMore(hasNext);
      hasMoreRef.current = hasNext;
      return newItems.length > 0;
    } catch (err) {
      console.error('Failed to load more videos:', err);
      return false;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setCurrentIndex(0);
    setPage(1);
    pageRef.current = 1;
    setHasMore(true);
    hasMoreRef.current = true;
    getVideosApi({ tab: activeTab, page: 1, limit: 100000 })
      .then((res) => {
        if (res && res.data && res.data.length > 0) {
          setVideos(res.data);
          const hasNext = res?.pagination?.has_next_page !== undefined
            ? res.pagination.has_next_page
            : false;
          setHasMore(hasNext);
          hasMoreRef.current = hasNext;
        } else {
          setVideos(MOCK_VIDEOS);
          setHasMore(false);
          hasMoreRef.current = false;
        }
      })
      .catch((err) => {
        console.error('Failed to load videos:', err);
        setVideos(MOCK_VIDEOS);
        setHasMore(false);
        hasMoreRef.current = false;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [activeTab]);

  useEffect(() => {
    setShowComments(false);
  }, [currentIndex]);

  // Jump to specific video when postId query param is present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const postId = params.get('postId');
    if (postId && videos.length > 0) {
      const idx = videos.findIndex(v => v._id === postId || v.post_id === postId);
      if (idx !== -1) {
        setCurrentIndex(idx);
      }
    }
  }, [location.search, videos]);

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play().catch(err => console.log('Autoplay blocked:', err));
      setIsPlaying(true);
    }
  };

  const handleNext = () => {
    if (currentIndex < videos.length - 1) {
      const nextIdx = currentIndex + 1;
      setCurrentIndex(nextIdx);
      setIsPlaying(true);
      if (nextIdx >= videos.length - 2 && hasMoreRef.current && !loadingMoreRef.current) {
        loadMoreVideos();
      }
    } else if (hasMoreRef.current && !loadingMoreRef.current) {
      loadMoreVideos().then((loaded) => {
        if (loaded) {
          setCurrentIndex(prev => prev + 1);
          setIsPlaying(true);
        }
      });
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setIsPlaying(true);
    }
  };

  const handleWheel = (e) => {
    if (wheelTimeout.current) return;

    if (e.deltaY > 30) {
      handleNext();
      wheelTimeout.current = setTimeout(() => { wheelTimeout.current = null; }, 800);
    } else if (e.deltaY < -30) {
      handlePrev();
      wheelTimeout.current = setTimeout(() => { wheelTimeout.current = null; }, 800);
    }
  };

  const handleTouchStart = (e) => {
    touchStartY.current = e.changedTouches[0].screenY;
  };

  const handleTouchEnd = (e) => {
    touchEndY.current = e.changedTouches[0].screenY;
    handleSwipe();
  };

  const handleSwipe = () => {
    const swipeDistance = touchStartY.current - touchEndY.current;
    const minSwipeDistance = 50;

    if (swipeDistance > minSwipeDistance) {
      handleNext();
    } else if (swipeDistance < -minSwipeDistance) {
      handlePrev();
    }
  };

  useEffect(() => {
    return () => {
      if (wheelTimeout.current) clearTimeout(wheelTimeout.current);
    };
  }, []);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      if (playerContainerRef.current?.requestFullscreen) {
        playerContainerRef.current.requestFullscreen().then(() => {
          if (window.screen.orientation && window.screen.orientation.lock) {
            window.screen.orientation.lock('landscape').catch((err) => {
              console.log('Orientation lock failed:', err);
            });
          }
        }).catch(err => {
          console.error(`Error attempting to enable full-screen mode: ${err.message}`);
        });
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        if (window.screen.orientation && window.screen.orientation.unlock) {
          window.screen.orientation.unlock();
        }
      }
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    navigate('/login', { state: { from: '/video-feed' } });
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    navigate('/login', { state: { from: '/video-feed' } });
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const currentVid = videos[currentIndex];
    if (!currentVid) return;

    try {
      const shareUrl = currentVid.share_url_web || currentVid.share_url || currentVid.share_url_app || `${window.location.origin}/video/${currentVid._id}`;
      await navigator.clipboard.writeText(shareUrl);
      setToastMessage('Link copied to clipboard!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    } catch (err) {
      console.error('Failed to copy', err);
    }
  };

  const handleUploadSuccess = () => {};

  const handleCommentClick = (e) => {
    e.stopPropagation();
    setShowComments(!showComments);
    if (!showComments) {
      // Use comments embedded in the video object since backend doesn't have a specific get endpoint
      setCommentsList(currentVid.comments || []);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    navigate('/login', { state: { from: '/video-feed' } });
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        handleNext();
      } else if (e.key === 'ArrowUp') {
        handlePrev();
      } else if (e.key === ' ') {
        e.preventDefault();
        handlePlayPause();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, videos]);

  if (loading) {
    return (
      <div className="vf-loading-container">
        <div className="vf-spinner"></div>
        <p>Loading Video Feed...</p>
      </div>
    );
  }

  const currentVid = videos[currentIndex];
  const videoSrc = currentVid?.video_url || currentVid?.url || currentVid?.path || '';
  const caption = currentVid?.caption || currentVid?.title || 'Untitled Video';
  const description = currentVid?.description || '';
  const creator = currentVid?.author || currentVid?.creator || currentVid?.teacher || {};
  const creatorName = creator?.full_name || creator?.name || 'Yoospeak Teacher';
  const creatorAvatar = creator?.profile_picture || null;
  const isVerified = creator?.is_verified ?? true;
  const viewsCount = currentVid?.view_count || currentVid?.views || 0;
  const likesCount = currentVid?.like_count || currentVid?.likes || 0;
  const isLiked = currentVid?.isLiked || false;
  const commentsCount = currentVid?.comment_count || currentVid?.comments || 0;
  const sharesCount = currentVid?.share_count || currentVid?.shares || 0;
  const savesCount = currentVid?.save_count || currentVid?.saves || 0;
  const isSaved = currentVid?.isSaved || false;
  const getTimeAgo = (date) => {
    const seconds = Math.floor((Date.now() - new Date(date)) / 1000);
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

    if (seconds < 60) return rtf.format(-seconds, 'second');
    if (seconds < 3600) return rtf.format(-Math.floor(seconds / 60), 'minute');
    if (seconds < 86400) return rtf.format(-Math.floor(seconds / 3600), 'hour');
    if (seconds < 2592000) return rtf.format(-Math.floor(seconds / 86400), 'day');
    if (seconds < 31536000) return rtf.format(-Math.floor(seconds / 2592000), 'month');
    return rtf.format(-Math.floor(seconds / 31536000), 'year');
  };

  const timeAgo = currentVid?.createdAt ? getTimeAgo(currentVid.createdAt) : 'N/A';

  const handleProfileClick = (e) => {
    e.stopPropagation();
    const creatorId = creator?._id || '1'; // Default fallback ID if not provided
    navigate('/teachers-details', { state: { id: creatorId } });
  };

  return (
    <div className="video-feed-page">
      <div className="container-fluid vf-layout">
        <div className="row g-0">

          {/* Left Sidebar */}
          <aside className="col-md-3 col-lg-3 d-none d-md-block vf-sidebar">
            <div className="vf-sidebar-nav">
              <button
                className={`vf-nav-item ${activeTab === 'Video Feed' ? 'active' : ''}`}
                onClick={() => setActiveTab('Video Feed')}
              >
                <i className="bi bi-play-btn-fill" /> Video Feed
              </button>
              <button
                className={`vf-nav-item ${activeTab === 'Following' ? 'active' : ''}`}
                onClick={() => setActiveTab('Following')}
              >
                <i className="bi bi-people" /> Following
              </button>
              <button
                className={`vf-nav-item ${activeTab === 'Trending' ? 'active' : ''}`}
                onClick={() => setActiveTab('Trending')}
              >
                <i className="bi bi-hand-thumbs-up" /> Trending
              </button>
              <button
                className={`vf-nav-item ${activeTab === 'Live' ? 'active' : ''}`}
                onClick={() => setActiveTab('Live')}
              >
                <i className="bi bi-camera-video" /> Live
              </button>
              <button
                className={`vf-nav-item ${activeTab === 'Saved' ? 'active' : ''}`}
                onClick={() => setActiveTab('Saved')}
              >
                <i className="bi bi-bookmark" /> Saved
              </button>
              <button
                className={`vf-nav-item ${activeTab === 'Liked Videos' ? 'active' : ''}`}
                onClick={() => setActiveTab('Liked Videos')}
              >
                <i className="bi bi-heart" /> Liked Videos
              </button>
            </div>

            {/* Create own video CTA */}
            <div className="vf-cta-card">
              <h6>Create your own video</h6>
              <p>Share your knowledge and inspire others!</p>
              <button className="vf-cta-btn" onClick={() => {
                navigate('/login', { state: { from: '/video-feed' } });
              }}>
                <i className="bi bi-cloud-arrow-up-fill" /> Upload Video
              </button>
            </div>
          </aside>

          {/* Main Video Viewport */}
          <main className="col-12 col-md-9 col-lg-9 vf-main">
            <div className="vf-player-container" onWheel={handleWheel} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>

              {/* Previous Video trigger button (Desktop navigation helper) */}
              {currentIndex > 0 && (
                <button className="vf-nav-arrow arrow-up" onClick={handlePrev} aria-label="Previous Video">
                  <i className="bi bi-chevron-up" />
                </button>
              )}

              {/* Next Video trigger button */}
              {(currentIndex < videos.length - 1 || (hasMore && !loading)) && (
                <button
                  className="vf-nav-arrow arrow-down"
                  onClick={handleNext}
                  aria-label="Next Video"
                  disabled={loadingMore && currentIndex >= videos.length - 1}
                >
                  {loadingMore && currentIndex >= videos.length - 1 ? (
                    <span className="spinner-border spinner-border-sm text-white" style={{ width: '1rem', height: '1rem' }} role="status" aria-hidden="true" />
                  ) : (
                    <i className="bi bi-chevron-down" />
                  )}
                </button>
              )}

              {/* Video Player Box */}
              <div className="vf-player" ref={playerContainerRef} onClick={handlePlayPause}>
                <video
                  ref={videoRef}
                  src={videoSrc}
                  className="vf-video-element"
                  autoPlay={isPlaying}
                  loop
                  muted={isMuted}
                  playsInline
                />

                {/* Top Right: Controls */}
                <div className="vf-top-controls" style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 5, display: 'flex', gap: '8px' }}>
                  <button className="vf-volume-toggle" onClick={toggleMute} aria-label="Toggle Mute" style={{ position: 'static' }}>
                    <i className={`bi ${isMuted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'}`} />
                  </button>
                  <button className="vf-volume-toggle" onClick={(e) => { e.stopPropagation(); toggleFullScreen(); }} aria-label="Toggle Fullscreen" style={{ position: 'static' }}>
                    <i className="bi bi-arrows-fullscreen" />
                  </button>
                </div>

                {/* Right Interactive Overlays */}
                <div className="vf-overlays-sidebar" onClick={(e) => e.stopPropagation()}>

                  {/* Like Button */}
                  <div className="vf-overlay-item">
                    <button className={`vf-action-btn ${isLiked ? 'like-btn' : ''}`} onClick={handleLike}>
                      <i className={isLiked ? "bi bi-heart-fill text-white" : "bi bi-heart-fill"} />
                    </button>
                    <span>{likesCount}</span>
                  </div>

                  {/* Comments Button */}
                  <div className="vf-overlay-item">
                    <button className="vf-action-btn" onClick={handleCommentClick}>
                      <i className="bi bi-chat-right-text-fill" />
                    </button>
                    <span>{commentsCount}</span>
                  </div>

                  {/* Share Button */}
                  <div className="vf-overlay-item">
                    <button className="vf-action-btn" onClick={handleShare}>
                      <i className="bi bi-reply-fill" style={{ display: 'inline-block', transform: 'scaleX(-1)' }} />
                    </button>
                    <span>{sharesCount}</span>
                  </div>

                  {/* Save Button */}
                  <div className="vf-overlay-item">
                    <button className="vf-action-btn" onClick={handleSave}>
                      <i className={isSaved ? "bi bi-bookmark-check-fill text-primary" : "bi bi-bookmark-fill"} />
                    </button>
                    <span>{savesCount}</span>
                  </div>

                  {/* More options dots */}
                  <div className="vf-overlay-item">
                    <button className="vf-action-btn more-btn">
                      <i className="bi bi-three-dots-vertical" />
                    </button>
                  </div>

                </div>

                {/* Left Bottom Video Details Overlay */}
                <div className="vf-details-overlay" onClick={(e) => e.stopPropagation()}>

                  {/* White caption title block */}
                  <div className="vf-caption-box">
                    <h2>{caption}</h2>
                  </div>

                  {/* Creator Info */}
                  <div className="vf-creator-info" onClick={handleProfileClick} style={{ cursor: 'pointer' }}>
                    <div className="vf-creator-avatar">
                      {creatorAvatar ? (
                        <img src={creatorAvatar} alt={creatorName} />
                      ) : (
                        <div className="vf-avatar-placeholder">
                          <i className="bi bi-person-fill" />
                        </div>
                      )}
                    </div>

                    <div className="vf-creator-meta">
                      <div className="vf-creator-name-row">
                        <span className="vf-creator-name">{creatorName}</span>
                        {isVerified && <i className="bi bi-patch-check-fill vf-verified-badge" />}
                      </div>
                      <div className="vf-video-sub-meta">
                        <span>{description.substring(0, 15)}...</span>
                        <span className="dot">â€¢</span>
                        <span>{timeAgo}</span>
                      </div>
                      <div className="vf-views-meta">
                        {viewsCount} Views
                      </div>
                    </div>
                  </div>

                </div>

                {/* Comments Overlay */}
                {showComments && (
                  <div className="vf-comments-overlay" onClick={(e) => e.stopPropagation()}>
                    <div className="vf-comments-header">
                      <h5>Comments ({commentsCount})</h5>
                      <button className="close-btn" onClick={() => setShowComments(false)}>
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                    <div className="vf-comments-body">
                      {loadingComments ? (
                        <div className="text-center p-3">
                          <div className="spinner-border spinner-border-sm text-secondary"></div>
                        </div>
                      ) : commentsList.length > 0 ? (
                        commentsList.map(c => (
                          <div key={c._id} className="vf-comment-item">
                            <strong>{c.user?.full_name || c.user?.name || 'User'}</strong>
                            <p>{c.comment || c.text}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted text-center mt-3">No comments yet. Be the first!</p>
                      )}
                    </div>
                    <form className="vf-comments-footer" onSubmit={handleAddComment}>
                      <input
                        type="text"
                        placeholder="Add a comment..."
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                      />
                      <button type="submit" disabled={!commentText.trim()}>
                        <i className="bi bi-send-fill"></i>
                      </button>
                    </form>
                  </div>
                )}

              </div>

            </div>

            {/* Custom Toast Notification */}
            {showToast && (
              <div className="vf-toast">
                <i className="bi bi-check-circle-fill"></i> {toastMessage}
              </div>
            )}
          </main>

        </div>
      </div>

      {/* Bottom Nav Bar for Mobile */}
      <div className="vf-mobile-nav d-flex d-md-none">
        <button className="vf-mobile-nav-btn">
          <i className="bi bi-house-door" />
          <span>HOME</span>
        </button>
        <button className="vf-mobile-nav-btn">
          <i className="bi bi-people" />
          <span>FRIENDS</span>
        </button>
        <button className="vf-mobile-nav-btn active">
          <i className="bi bi-play-circle" />
          <span>COMMUNITY</span>
        </button>
        <button className="vf-mobile-nav-btn">
          <i className="bi bi-mortarboard" />
          <span>TEACHERS</span>
        </button>
        <button className="vf-mobile-nav-btn">
          <i className="bi bi-person" />
          <span>PROFILE</span>
        </button>
      </div>

    </div>
  );
};
